import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Package, Sparkles, ShoppingCart } from 'lucide-react';
import { motion } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { trackBundleView, trackBundleAddToCart } from '../utils/tracking';

function BundlesPage() {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchBundles();
  }, []);

  const fetchBundles = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bundles`);
      const data = await response.json();
      
      // Fetch details for each bundle to get products
      const bundlesWithProducts = await Promise.all(
        (data.bundles || []).map(async (bundle) => {
          try {
            const detailRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bundles/${bundle.id}`);
            const detailData = await detailRes.json();
            return detailData;
          } catch (err) {
            return bundle;
          }
        })
      );
      
      setBundles(bundlesWithProducts);
    } catch (error) {
      console.error('Failed to fetch bundles:', error);
      toast.error('Failed to load bundles');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBundle = async (bundle) => {
    try {
      trackBundleView(bundle.id, bundle.name);
      
      // Bundle already has products loaded, use them directly
      if (bundle.products && bundle.products.length > 0) {
        // Add bundle as single cart item with bundle pricing
        const bundleCartItem = {
          id: bundle.id,
          title: bundle.name,
          description: bundle.description,
          price: bundle.price,
          original_price: bundle.original_price,
          savings: bundle.savings,
          image_urls: bundle.products.map(p => p.image_urls?.[0]).filter(Boolean),
          isBundle: true,
          bundleProducts: bundle.products,
          productCount: bundle.products.length
        };
        
        addToCart(bundleCartItem);
        trackBundleAddToCart(bundle.id, bundle.name, bundle.price);
        toast.success(`${bundle.name} added to cart!`, {
          description: `Bundle with ${bundle.products.length} items - ₹${bundle.price}`
        });
      } else {
        toast.info('Bundle coming soon!', {
          description: 'Products will be added shortly'
        });
      }
    } catch (error) {
      console.error('Failed to add bundle:', error);
      toast.error('Failed to add bundle');
    }
  };

  return (
    <div className="min-h-screen py-6 px-4 pb-20 md:pb-8">
      <Helmet>
        <title>Product Bundles - Save More | Mothering</title>
        <meta name="description" content="Shop curated product bundles for kids. Save more with our ready-made kits for school, sports, birthdays and more." />
      </Helmet>

      <div className="container mx-auto max-w-6xl">
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2 mb-2">
            <Package className="w-8 h-8 text-cyan-600" />
            Curated Bundles
          </h1>
          <p className="text-muted-foreground">Save more with our ready-made kits for every occasion</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-96 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : bundles.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-20 h-20 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No bundles available yet</h3>
            <p className="text-muted-foreground">Check back soon for amazing bundle deals!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {bundles.map((bundle) => (
              <motion.div
                key={bundle.id}
                whileHover={{ y: -4 }}
                transition={{ duration: 0.2 }}
              >
                <Card className="overflow-hidden border-2 border-cyan-100 hover:border-cyan-300 transition-colors h-full">
                  <CardContent className="p-0">
                    <div className="relative">
                      {/* Product Image Collage */}
                      {bundle.products && bundle.products.length > 0 ? (
                        <div className="grid grid-cols-2 gap-1 h-48 md:h-56 bg-gray-100">
                          {bundle.products.slice(0, 4).map((product, idx) => (
                            <div 
                              key={idx} 
                              className={`overflow-hidden ${
                                bundle.products.length === 1 ? 'col-span-2' :
                                bundle.products.length === 3 && idx === 0 ? 'col-span-2' : ''
                              }`}
                            >
                              <img
                                src={product.image_urls?.[0] || bundle.image_url}
                                alt={product.title}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <img
                          src={bundle.image_url}
                          alt={bundle.name}
                          className="w-full h-48 md:h-56 object-cover"
                          loading="lazy"
                        />
                      )}
                      <div className="absolute top-2 right-2">
                        <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white border-0">
                          Save ₹{bundle.savings}
                        </Badge>
                      </div>
                      <div className="absolute top-2 left-2">
                        <Badge variant="secondary" className="bg-white/90 backdrop-blur">
                          <Sparkles className="w-3 h-3 mr-1" />
                          Bundle
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="p-4 space-y-3">
                      <div>
                        <h3 className="font-bold text-lg mb-1">{bundle.name}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {bundle.description}
                        </p>
                      </div>
                      
                      {bundle.age_groups && bundle.age_groups.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {bundle.age_groups.map((age) => (
                            <Badge key={age} variant="outline" className="text-xs">
                              {age} yrs
                            </Badge>
                          ))}
                        </div>
                      )}
                      
                      <div className="space-y-2">
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-bold text-cyan-600">₹{bundle.price}</span>
                          <span className="text-lg text-gray-400 line-through">₹{bundle.original_price}</span>
                        </div>
                        <p className="text-sm text-green-600 font-semibold">
                          {Math.round((bundle.savings / bundle.original_price) * 100)}% OFF - Save ₹{bundle.savings}
                        </p>
                        {bundle.products && bundle.products.length > 0 && (
                          <p className="text-xs text-muted-foreground">
                            {bundle.products.length} items included
                          </p>
                        )}
                      </div>
                      
                      <Button
                        onClick={() => handleAddBundle(bundle)}
                        className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 gap-2"
                        size="lg"
                      >
                        <ShoppingCart className="w-4 h-4" />
                        Add Bundle to Cart
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default BundlesPage;
